/**
 * 처방 판정 테스트 패널 — 입력 폼 순수 로직(설계 결정 9·13).
 *
 * 화면(조립)과 분리해 단위 테스트로 고정한다:
 * - 입력 → 요청 변환(빈 칸 키 생략, 숫자 변환, 코드 필드 선택)
 * - 실행 가능 판정(행 0·투약량/횟수 필수·양수·NaN/Infinity 차단, 체중 0 차단)
 * - 입력 폼 값 비교("다시 실행하세요" 띠) — 요청이 아니라 폼 문자열로 비교(실행 불가 입력도 비교 가능)
 * - 년→월령 보조 변환, 약품 선택/이름 수정 시 행 코드 처리
 */
import type { Drug } from "common/models/types";
import type {
  DoseReviewRequest,
  DoseReviewPatientInput,
  DoseReviewPrescriptionInput,
} from "api/doseReviewService";

/** 약품 표 한 행의 입력 상태(전부 문자열 — DOM 입력값 그대로). */
export interface RowState {
  id: string; // React key 전용(요청·비교에 미포함)
  medicationName: string;
  standardCode: string;
  insuranceCode: string;
  itemCode: string; // 엔진 미사용 · DrugSearchInput "선택됨" 판정 키(현황 2)
  dose: string; // 1회 투약량
  freq: string; // 1일 횟수
}

export interface FormState {
  weight: string; // kg
  ageMonths: string; // 정본
  ageYears: string; // 보조 — 요청에 싣지 않는다
  rows: RowState[];
}

/**
 * 약품 표 열 라벨(AC-28). 투약일수·단위 칸을 두지 않는다 — 엔진이 쓰지 않아 눌러도
 * 결과가 안 바뀌는 칸이기 때문이다(설계 결정 9·플로우 5).
 */
export const ROW_COLUMN_LABELS = ["의약품", "1회 투약량", "1일 횟수", "삭제"] as const;

export const emptyRow = (id: string): RowState => ({
  id,
  medicationName: "",
  standardCode: "",
  insuranceCode: "",
  itemCode: "",
  dose: "",
  freq: "",
});

export const emptyForm = (firstRowId: string): FormState => ({
  weight: "",
  ageMonths: "",
  ageYears: "",
  rows: [emptyRow(firstRowId)],
});

// ── 숫자 검증(설계 결정 13 ②) ──

/** 빈칸/음수/0/NaN/Infinity면 false. 소수 허용. */
const isPositiveFinite = (s: string): boolean => {
  const t = s.trim();
  if (t === "") return false;
  const n = Number(t);
  return Number.isFinite(n) && n > 0;
};

/** 입력됐을 때만 유한 양수(빈칸 허용). 체중용 — 0은 서버 400. */
const positiveFiniteIfPresent = (s: string): boolean => {
  const t = s.trim();
  if (t === "") return true;
  const n = Number(t);
  return Number.isFinite(n) && n > 0;
};

/** 입력됐을 때만 유한 0 이상(빈칸 허용). 월령용. */
const nonNegFiniteIfPresent = (s: string): boolean => {
  const t = s.trim();
  if (t === "") return true;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0;
};

/** 빈칸이면 null, 유한하면 number, 그 외 null(빈칸은 요청에서 키 생략). */
const parsePresentNumber = (s: string): number | null => {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/**
 * 실행 가능 판정(AC-20~24b).
 * - 약품 행 0개 → 불가
 * - 각 행 1회 투약량·1일 횟수는 필수이고 유한 양수(소수 OK, 0 이하·NaN·Infinity 불가)
 * - 체중은 있으면 유한 양수(0 불가), 월령은 있으면 유한 0 이상
 */
export const canRunReview = (form: FormState): boolean => {
  if (form.rows.length === 0) return false;
  if (!positiveFiniteIfPresent(form.weight)) return false;
  if (!nonNegFiniteIfPresent(form.ageMonths)) return false;
  for (const r of form.rows) {
    if (!isPositiveFinite(r.dose)) return false;
    if (!isPositiveFinite(r.freq)) return false;
  }
  return true;
};

/**
 * 입력 → 요청 변환(설계 결정 9, AC-25~27b·29·30).
 * 빈 체중·월령은 키 생략. 요청에는 standardCode·insuranceCode를 싣고 itemCode는 싣지 않는다.
 * 숫자 칸은 number로 싣는다. canRunReview가 true인 상태에서 호출하는 것을 전제한다.
 */
export const toRequest = (form: FormState): DoseReviewRequest => {
  const patient: DoseReviewPatientInput = {};
  const w = parsePresentNumber(form.weight);
  if (w !== null) patient.weightKg = w;
  const a = parsePresentNumber(form.ageMonths);
  if (a !== null) patient.ageMonths = a;

  const prescriptions: DoseReviewPrescriptionInput[] = form.rows.map((r) => {
    const p: DoseReviewPrescriptionInput = {};
    const std = r.standardCode.trim();
    const ins = r.insuranceCode.trim();
    const name = r.medicationName.trim();
    if (std) p.standardCode = std;
    if (ins) p.insuranceCode = ins;
    if (name) p.medicationName = name;
    const dose = parsePresentNumber(r.dose);
    if (dose !== null) p.dosagePerAdministration = dose;
    const freq = parsePresentNumber(r.freq);
    if (freq !== null) p.timesPerDay = freq;
    return p;
  });

  const req: DoseReviewRequest = { prescriptions };
  if (Object.keys(patient).length > 0) req.patient = patient;
  return req;
};

/** 코드 없이 이름만 있는 행 — code_missing 경고(AC-31) 대상. */
export const rowNeedsCodeWarning = (r: RowState): boolean =>
  r.standardCode.trim() === "" && r.insuranceCode.trim() === "" && r.medicationName.trim() !== "";

// ── 폼 값 비교(설계 결정 13 ⑥, AC-53b) ──

const SEP = "";

/**
 * 입력 폼 값 서명 — 각 칸 trim, 코드 3종 포함. 요청이 아니라 폼 값으로 비교하므로
 * NaN·Infinity 같은 실행 불가 입력도 예외 없이 비교된다. 년 보조 칸은 월령에 반영되므로 제외.
 */
export const formSignature = (form: FormState): string => {
  const patient = [form.weight, form.ageMonths].map((s) => s.trim());
  const rows = form.rows.map((r) =>
    [r.medicationName, r.standardCode, r.insuranceCode, r.itemCode, r.dose, r.freq]
      .map((s) => s.trim())
      .join(SEP)
  );
  return JSON.stringify([patient, rows]);
};

// ── 년↔월령·약품 선택·이름 수정(설계 결정 13 ④, 9) ──

/** 년 × 12를 월령 문자열로. 빈칸·비유한이면 빈 문자열. */
export const yearsToMonths = (years: string): string => {
  const t = years.trim();
  if (t === "") return "";
  const n = Number(t);
  if (!Number.isFinite(n)) return "";
  return String(n * 12);
};

/** 년 보조 칸 입력 — 월령 칸을 년×12로 채운다(AC-27). */
export const setAgeYears = (form: FormState, value: string): FormState => ({
  ...form,
  ageYears: value,
  ageMonths: yearsToMonths(value),
});

/** 월령 칸 직접 수정 — 년 보조 칸을 비운다(AC-27b). */
export const setAgeMonths = (form: FormState, value: string): FormState => ({
  ...form,
  ageMonths: value,
  ageYears: "",
});

/** 약품 검색 선택 — 코드 3종·이름을 채운다(dose/freq는 유지). */
export const rowFromDrug = (row: RowState, drug: Drug): RowState => ({
  ...row,
  medicationName: drug.productName ?? "",
  standardCode: drug.standardCode ?? "",
  insuranceCode: drug.insuranceCode ?? "",
  itemCode: drug.itemCode ?? "",
});

/** 이름 직접 수정 — 코드 3종을 모두 비운다(설계 결정 9, AC-30). */
export const rowWithName = (row: RowState, name: string): RowState => ({
  ...row,
  medicationName: name,
  standardCode: "",
  insuranceCode: "",
  itemCode: "",
});
